import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import toast from 'react-hot-toast';

export default function Assignments() {
  const [tenantId, setTenantId] = useState<string>('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [assignments, setAssignments] = useState<any[]>([]);
  const [userAssignedUsers, setUserAssignedUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUserAssignments, setLoadingUserAssignments] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [m365Groups, setM365Groups] = useState<any[]>([]);
  const [loadingM365Groups, setLoadingM365Groups] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [processingGroups, setProcessingGroups] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [processingUsers, setProcessingUsers] = useState(false);
  const [userAssignments, setUserAssignments] = useState<any[]>([]);
  const [showSyncGroupsModal, setShowSyncGroupsModal] = useState(false);
  const [availableM365Groups, setAvailableM365Groups] = useState<any[]>([]);
  const [syncGroupsSearch, setSyncGroupsSearch] = useState('');
  const [fetchingM365Groups, setFetchingM365Groups] = useState(false);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [m365Users, setM365Users] = useState<any[]>([]);
  const [loadingM365Users, setLoadingM365Users] = useState(false);
const [syncingUsers, setSyncingUsers] = useState(false);
const [activeTab, setActiveTab] = useState<'groups' | 'users'>('groups');
const [templateSearch, setTemplateSearch] = useState('');
const [templateAssignmentCounts, setTemplateAssignmentCounts] = useState<Map<string, number>>(new Map());
const [editingPriority, setEditingPriority] = useState<string | null>(null);
const [editPriorityValue, setEditPriorityValue] = useState<number>(0);
const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
const [selectedGroupMembers, setSelectedGroupMembers] = useState<any[]>([]);
const [selectedGroupName, setSelectedGroupName] = useState('');
const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
const [processingMessage, setProcessingMessage] = useState('Processing...');
const [showProcessingModal, setShowProcessingModal] = useState(false);

  // Get current user's tenant on mount
  useEffect(() => {
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  // Load templates and groups when tenant changes
  useEffect(() => {
    if (tenantId) {
      loadTemplates();
      loadGroups();
      loadStats();
    }
  }, [tenantId]);

  // Load assignments when template changes
  useEffect(() => {
    if (selectedTemplate) {
      loadUserAssignments();
      loadAssignedUsers();
    }
  }, [selectedTemplate]);

  const loadTemplates = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/templates?tenantId=${tenantId}`);
      if (data.success) {
        setTemplates(data.data);
        if (data.data.length > 0) {
          setSelectedTemplate(data.data[0].id);
        }
        // Load assignment counts for all templates
        await loadAllTemplateAssignmentCounts(data.data);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadAllTemplateAssignmentCounts = async (templateList: any[]) => {
    try {
      const counts = new Map<string, number>();
      for (const template of templateList) {
        // Load both group and user assignments for each template
        const [groupData, userData] = await Promise.all([
          apiClient.get(`/api/assignments/template/${template.id}`),
          apiClient.get(`/api/assignments/template/${template.id}/direct-users`),
        ]);
        const groupCount = groupData.success ? groupData.data.length : 0;
        const userCount = userData.success ? userData.data.length : 0;
        counts.set(template.id, groupCount + userCount);
      }
      setTemplateAssignmentCounts(counts);
    } catch (error) {
      console.error('Failed to load template assignment counts:', error);
    }
  };

  const loadGroups = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/groups/${tenantId}`);
      if (data.success) {
        setGroups(data.data);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleUnassignUser = async (userId: string) => {
    try {
      const data = await apiClient.post('/api/assignments/unassign-user', {
        userId,
        templateId: selectedTemplate,
      });
      if (data.success) {
        toast.success('User unassigned successfully!');
        await loadAssignedUsers();
        await loadStats();
        await loadAllTemplateAssignmentCounts(templates);
      } else {
        toast.error(`Unassignment failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`Unassignment failed: ${error.message}`);
    }
  };

  const loadUserAssignments = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/assignments/template/${selectedTemplate}`);
      if (data.success) {
        setAssignments(data.data);
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
    setLoading(false);
  };

  const loadAssignedUsers = async () => {
    if (!selectedTemplate) return;
    setLoadingUserAssignments(true);
    try {
      // Use efficient endpoint that only returns users with direct assignments
      const data = await apiClient.get(`/api/assignments/template/${selectedTemplate}/direct-users`);
      if (data.success) {
        setUserAssignedUsers(data.data);
      }
    } catch (error) {
      console.error('Failed to load assigned users:', error);
    }
    setLoadingUserAssignments(false);
  };

  const loadStats = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/assignments/stats/${tenantId}`);
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleAssign = async (groupId: string, priority: number) => {
    try {
      const data = await apiClient.post('/api/assignments/assign', {
        groupId,
        templateId: selectedTemplate,
        priority,
      });
      if (data.success) {
        loadUserAssignments();
        loadStats();
      } else {
        toast.error(`Assignment failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`Assignment failed: ${error.message}`);
    }
  };

  const handleUnassign = async (groupId: string) => {
    try {
      const data = await apiClient.post('/api/assignments/unassign', {
        groupId,
        templateId: selectedTemplate,
      });
      if (data.success) {
        loadUserAssignments();
        loadStats();
        await loadAllTemplateAssignmentCounts(templates);
      }
    } catch (error: any) {
      toast.error(`Unassignment failed: ${error.message}`);
    }
  };

  const handleUpdatePriority = async (assignmentId: string, groupId: string, newPriority: number) => {
    try {
      const data = await apiClient.post('/api/assignments/assign', {
        groupId,
        templateId: selectedTemplate,
        priority: newPriority,
      });
      if (data.success) {
        toast.success('Priority updated successfully!');
        await loadUserAssignments();
        setEditingPriority(null);
      } else {
        toast.error(`Update failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`Update failed: ${error.message}`);
    }
  };

  const loadM365Groups = async () => {
    setLoadingM365Groups(true);
    try {
      const data = await apiClient.get(`/api/groups/available/${tenantId}`);
      if (data.success) {
        const sortedGroups = data.data.sort((a: any, b: any) => 
          a.displayName.localeCompare(b.displayName)
        );
        setM365Groups(sortedGroups);
      }
    } catch (error: any) {
      toast.error(`Failed to load groups: ${error.message}`);
    }
    setLoadingM365Groups(false);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await apiClient.get(`/api/users/${tenantId}?limit=1000&includeAssignments=true`);
      if (data.success) {
        setUsers(data.data);
      }
    } catch (error: any) {
      toast.error(`Failed to load users: ${error.message}`);
    }
    setLoadingUsers(false);
  };

  const loadM365Users = async () => {
    setLoadingM365Users(true);
    try {
      const data = await apiClient.get(`/api/users/available/${tenantId}`);
      if (data.success) {
        setM365Users(data.data);
      }
    } catch (error: any) {
      toast.error(`Failed to load Microsoft 365 users: ${error.message}`);
    }
    setLoadingM365Users(false);
  };

  const fetchAvailableM365Groups = async () => {
    setFetchingM365Groups(true);
    try {
      const availableData = await apiClient.get(`/api/groups/available/${tenantId}`);
      const syncedData = await apiClient.get(`/api/groups/${tenantId}?limit=1000`);
      
      if (availableData.success) {
        const sortedGroups = availableData.data.sort((a: any, b: any) => 
          a.displayName.localeCompare(b.displayName)
        );
        setAvailableM365Groups(sortedGroups);
        
        // Pre-select groups that are already synced
        if (syncedData.success) {
          const syncedGroupIds = new Set(
            syncedData.data.map((g: any) => g.graphGroupId)
          );
          setSelectedGroupIds(syncedGroupIds);
        } else {
          setSelectedGroupIds(new Set());
        }
        
        setShowSyncGroupsModal(true);
        setSyncGroupsSearch('');
      } else {
        toast.error(`Failed to fetch groups: ${availableData.error}`);
      }
    } catch (error: any) {
      toast.error(`Failed to fetch groups: ${error.message}`);
    }
    setFetchingM365Groups(false);
  };

  const handleSyncGroups = async () => {
    if (!tenantId || selectedGroupIds.size === 0) {
      toast.error('Please select at least one group to sync');
      return;
    }

    setProcessingMessage('Syncing groups and members...');
    setShowProcessingModal(true);
    setSyncingGroups(true);
    try {
      const data = await apiClient.post(`/api/groups/sync/${tenantId}`, {
        groupIds: Array.from(selectedGroupIds),
      });

      if (data.success) {
        toast.success(
          `Sync complete! Groups: ${data.data.groupsSync.synced} synced. Members: ${data.data.membersSync.totalAdded} added.`
        );
        setShowSyncGroupsModal(false);
        setAvailableM365Groups([]);
        setSelectedGroupIds(new Set());
        await loadGroups();
        if (selectedTemplate) {
          await loadUserAssignments();
        }
      } else {
        toast.error(`Sync failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`Sync failed: ${error.message}`);
    }
    setSyncingGroups(false);
    setShowProcessingModal(false);
  };

  const toggleGroupSelection = (groupId: string) => {
    const newSelection = new Set(selectedGroupIds);
    if (newSelection.has(groupId)) {
      newSelection.delete(groupId);
    } else {
      newSelection.add(groupId);
    }
    setSelectedGroupIds(newSelection);
  };

  const handleSyncUsers = async () => {
    if (!tenantId) return;
    setSyncingUsers(true);
    try {
      const data = await apiClient.post(`/api/users/sync/${tenantId}`);
      if (data.success) {
        toast.success(`Synced ${data.data.synced} users successfully!`);
      } else {
        toast.error(`User sync failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`User sync failed: ${error.message}`);
    }
    setSyncingUsers(false);
  };


  const toggleUserSelection = (graphUserId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(graphUserId)) {
      newSelection.delete(graphUserId);
    } else {
      newSelection.add(graphUserId);
    }
    setSelectedUserIds(newSelection);
  };

  const processSelectedGroups = async () => {
    if (selectedGroupIds.size === 0) {
      toast.error('Please select at least one group');
      return;
    }

    setProcessingMessage('Assigning groups and syncing members...');
    setShowProcessingModal(true);
    setProcessingGroups(true);
    const errors: string[] = [];

    for (const graphGroupId of selectedGroupIds) {
      try {
        const m365Group = m365Groups.find(g => g.id === graphGroupId);
        const groupName = m365Group?.displayName || graphGroupId;
        const alreadyAssigned = assignedGroupGraphIds.has(graphGroupId);

        if (alreadyAssigned) continue; // Skip already assigned

        // Sync group
        const syncData = await apiClient.post(`/api/groups/sync-single`, {
          tenantId,
          graphGroupId,
        });

        if (!syncData.success) {
          errors.push(`${groupName}: ${syncData.error}`);
          continue;
        }

        // Assign to template
        const assignData = await apiClient.post('/api/assignments/assign', {
          groupId: syncData.data.group.id,
          templateId: selectedTemplate,
          priority: 10,
        });

        if (!assignData.success) {
          errors.push(`${groupName}: ${assignData.error}`);
        }
      } catch (error: any) {
        const m365Group = m365Groups.find(g => g.id === graphGroupId);
        const groupName = m365Group?.displayName || graphGroupId;
        errors.push(`${groupName}: ${error.message}`);
      }
    }

    if (errors.length > 0) {
      toast.error(`Some operations failed:\n${errors.join('\n')}`);
    } else {
      toast.success(`${selectedGroupIds.size} group(s) assigned successfully!`);
    }

    // Trigger immediate sync to fetch group members from M365
    try {
      const syncResult = await apiClient.post(`/api/groups/sync/${tenantId}`);
      if (syncResult.success) {
        toast.success(
          `Members synced! ${syncResult.data.membersSync.totalAdded} users added.`
        );
      }
    } catch (error: any) {
      console.error('Failed to sync group members:', error);
      // Don't show error toast - assignment was successful
    }

    await loadGroups();
    await loadUserAssignments();
    await loadStats();
    await loadM365Groups();
    await loadAllTemplateAssignmentCounts(templates);
    setSelectedGroupIds(new Set());
    setProcessingGroups(false);
    setShowProcessingModal(false);
  };

  const processSelectedUsers = async () => {
    if (selectedUserIds.size === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setProcessingMessage('Assigning users to template...');
    setShowProcessingModal(true);
    setProcessingUsers(true);
    const errors: string[] = [];
    const assignedUserGraphIds = new Set(userAssignedUsers.map(u => u.graphUserId));

    for (const graphUserId of selectedUserIds) {
      try {
        const m365User = m365Users.find(u => u.id === graphUserId);
        const userName = m365User?.displayName || graphUserId;
        const alreadyAssigned = assignedUserGraphIds.has(graphUserId);

        if (alreadyAssigned) continue; // Skip already assigned

        const data = await apiClient.post('/api/assignments/assign-user', {
          userId: graphUserId,
          templateId: selectedTemplate,
          priority: 100,
        });

        if (!data.success) {
          errors.push(`${userName}: ${data.error}`);
        }
      } catch (error: any) {
        const m365User = m365Users.find(u => u.id === graphUserId);
        const userName = m365User?.displayName || graphUserId;
        errors.push(`${userName}: ${error.message}`);
      }
    }

    if (errors.length > 0) {
      toast.error(`Some operations failed:\n${errors.join('\n')}`);
    } else {
      toast.success(`${selectedUserIds.size} user(s) assigned successfully!`);
    }

    await loadUserAssignments();
    await loadAssignedUsers();
    await loadStats();
    await loadM365Users();
    await loadAllTemplateAssignmentCounts(templates);
    setSelectedUserIds(new Set());
    setProcessingUsers(false);
    setShowProcessingModal(false);
  };

  const filteredTemplates = templateSearch
    ? templates.filter((t) =>
        t.name.toLowerCase().includes(templateSearch.toLowerCase())
      )
    : templates;

  // Helper function to check if a template has any assignments
  const hasAssignments = (templateId: string) => {
    const count = templateAssignmentCounts.get(templateId) || 0;
    return count > 0;
  };

  const handleViewGroupMembers = async (groupId: string, groupName: string) => {
    setSelectedGroupName(groupName);
    setShowGroupMembersModal(true);
    setLoadingGroupMembers(true);
    
    try {
      const data = await apiClient.get(`/api/groups/details/${groupId}`);
      if (data.success) {
        setSelectedGroupMembers(data.data.members || []);
      }
    } catch (error) {
      console.error('Failed to load group members:', error);
      toast.error('Failed to load group members');
    }
    setLoadingGroupMembers(false);
  };

  const filteredM365Groups = groupSearch
    ? m365Groups.filter((g) =>
        g.displayName.toLowerCase().includes(groupSearch.toLowerCase()) ||
        (g.description && g.description.toLowerCase().includes(groupSearch.toLowerCase()))
      )
    : m365Groups;

  const assignedGroupGraphIds = new Set(assignments.map((a) => a.group.graphGroupId));
  const filteredUsers = userSearch
    ? m365Users.filter((u) =>
        u.displayName?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase())
      )
    : m365Users;

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 10px' }}>Template Assignments</h2>
        <p style={{ color: '#666', margin: 0 }}>
          Assign email signature templates to groups with priority rules. Signatures are deployed automatically.
        </p>
      </div>

      {/* Template Selector */}
      <div
        style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '30px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>Select Template</h3>
          {templates.length > 4 && (
            <input
              type="text"
              placeholder="Search templates..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                fontSize: '13px',
                width: '220px',
              }}
            />
          )}
        </div>
        {templates.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            No templates available
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template.id)}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: selectedTemplate === template.id ? '2px solid #667eea' : '2px solid #e5e7eb',
                  background: selectedTemplate === template.id ? '#f5f3ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (selectedTemplate !== template.id) {
                    e.currentTarget.style.borderColor = '#c7d2fe';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTemplate !== template.id) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {selectedTemplate === template.id && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}>
                    ✓
                  </div>
                )}
                <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px', marginBottom: '4px' }}>
                  {template.name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {hasAssignments(template.id) ? (
                    <span style={{ color: '#059669' }}>● Assigned</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>○ Not Assigned</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {templateSearch && filteredTemplates.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            No templates found matching "{templateSearch}"
          </div>
        )}
      </div>


      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => setActiveTab('groups')}
          style={{
            padding: '8px 14px',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            background: activeTab === 'groups' ? '#111827' : 'white',
            color: activeTab === 'groups' ? 'white' : '#111827',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Group Assignments
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '8px 14px',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            background: activeTab === 'users' ? '#111827' : 'white',
            color: activeTab === 'users' ? 'white' : '#111827',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Individual Users
        </button>
      </div>

      {activeTab === 'groups' ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Currently Assigned Groups ({assignments.length})
            </h3>
            <button
              onClick={async () => {
                setShowGroupModal(true);
                await loadM365Groups();
              }}
              style={{
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + Assign to Group
            </button>
          </div>
          <div>
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                maxHeight: '600px',
                overflowY: 'auto',
              }}
            >
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading assignments...
                </div>
              ) : assignments.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No groups assigned to this template yet.
                </div>
              ) : (
                assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    style={{
                      padding: '15px 20px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div 
                        style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => handleViewGroupMembers(assignment.group.id, assignment.group.name)}
                      >
                        {assignment.group.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>Priority:</span>
                        {editingPriority === assignment.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="number"
                              value={editPriorityValue}
                              onChange={(e) => setEditPriorityValue(parseInt(e.target.value) || 0)}
                              style={{
                                width: '50px',
                                padding: '3px 6px',
                                borderRadius: '4px',
                                border: '2px solid #667eea',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleUpdatePriority(assignment.id, assignment.group.id, editPriorityValue);
                                if (e.key === 'Escape') setEditingPriority(null);
                              }}
                            />
                            <button
                              onClick={() => handleUpdatePriority(assignment.id, assignment.group.id, editPriorityValue)}
                              style={{
                                width: '24px',
                                height: '24px',
                                padding: 0,
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="Save (Enter)"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => setEditingPriority(null)}
                              style={{
                                width: '24px',
                                height: '24px',
                                padding: 0,
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="Cancel (Esc)"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <span
                            onClick={() => {
                              setEditingPriority(assignment.id);
                              setEditPriorityValue(assignment.priority);
                            }}
                            style={{
                              fontWeight: '600',
                              color: '#667eea',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: '1px dashed #667eea',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f3ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                            title="Click to edit priority"
                          >
                            {assignment.priority}
                          </span>
                        )}
                        <span>• {assignment.group._count?.members || 0} members</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnassign(assignment.group.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                      }}
                    >
                      Unassign
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Currently Assigned Users ({userAssignedUsers.length})
            </h3>
            <button
              onClick={async () => {
                setShowUserModal(true);
                await loadM365Users();
                await loadUsers();
              }}
              style={{
                padding: '10px 20px',
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + Assign to User
            </button>
          </div>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            {loadingUserAssignments ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading user assignments...
              </div>
            ) : userAssignedUsers.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No users directly assigned to this template.
              </div>
            ) : (
              userAssignedUsers.map((user) => (
                <div
                  key={user.id}
                  style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                      {user.displayName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {user.email}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnassignUser(user.id)}
                    style={{
                      padding: '6px 12px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                    }}
                  >
                    Unassign
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      

      {/* Group Assignment Modal */}
      {showGroupModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowGroupModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Assign Group to Template</h3>
              <button
                onClick={() => setShowGroupModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Select groups to assign to this template. Click OK when done.
            </p>

            {/* Search */}
            <input
              type="text"
              placeholder="Search groups by name or description..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                borderRadius: '6px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                marginBottom: '15px',
              }}
            />

            {loadingM365Groups ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading groups from Microsoft 365...</div>
            ) : filteredM365Groups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                {groupSearch ? 'No groups found matching your search.' : 'No groups found'}
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                  {filteredM365Groups.map((group) => {
                    const alreadyAssigned = assignedGroupGraphIds.has(group.id);
                    const isSelected = selectedGroupIds.has(group.id);
                    return (
                      <div
                        key={group.id}
                        style={{
                          padding: '15px 20px',
                          background: 'white',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: alreadyAssigned ? 0.6 : 1,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', marginBottom: '3px' }}>
                            {group.displayName}
                            {alreadyAssigned && <span style={{ marginLeft: '8px', color: '#059669', fontSize: '13px', fontWeight: '600' }}>✓ Assigned</span>}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {group.description || 'No description'}
                          </div>
                        </div>
                        {alreadyAssigned ? (
                          <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>Already assigned</div>
                        ) : (
                          <button
                            onClick={() => toggleGroupSelection(group.id)}
                            style={{
                              padding: '8px 16px',
                              background: isSelected ? '#2563eb' : '#e5e7eb',
                              color: isSelected ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: '500',
                            }}
                          >
                            {isSelected ? '✓ Selected' : 'Select'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setShowGroupModal(false);
                      setSelectedGroupIds(new Set());
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processSelectedGroups}
                    disabled={processingGroups || selectedGroupIds.size === 0}
                    style={{
                      padding: '10px 20px',
                      background: processingGroups || selectedGroupIds.size === 0 ? '#9ca3af' : '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: processingGroups || selectedGroupIds.size === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {processingGroups ? 'Processing...' : `OK (${selectedGroupIds.size})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Assignment Modal */}
      {showUserModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowUserModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Assign Template Directly to User</h3>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Select users to assign directly to this template. Direct user assignments always override group assignments. Each user can only be assigned to ONE template directly.
            </p>

            {/* Search */}
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                borderRadius: '6px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                marginBottom: '15px',
              }}
            />

            {loadingM365Users ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading users from Microsoft 365...</div>
            ) : m365Users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>👤</div>
                <h4 style={{ margin: '0 0 12px', color: '#374151', fontSize: '18px', fontWeight: '600' }}>
                  No Licensed Users Available
                </h4>
                <p style={{ color: '#6b7280', marginBottom: '20px', lineHeight: '1.6' }}>
                  Template assignments are only available for licensed users.<br />
                  Please assign licenses to users first on the License Management page.
                </p>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    window.location.href = '/license-management';
                  }}
                  style={{
                    padding: '10px 24px',
                    background: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Go to License Management
                </button>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No users found matching your search.
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                  {filteredUsers.map((user) => {
                    // Check if user is assigned to THIS template
                    const assignedUserGraphIds = new Set(userAssignedUsers.map(u => u.graphUserId));
                    const isAssignedToThisTemplate = assignedUserGraphIds.has(user.id);
                    // Check if user has ANY direct assignment (stored in DB users)
                    const dbUser = users.find(u => u.graphUserId === user.id);
                    const hasAnyAssignment = dbUser?.templateAssignments && dbUser.templateAssignments.length > 0;
                    const isSelected = selectedUserIds.has(user.id);
                    return (
                      <div
                        key={user.id}
                        style={{
                          padding: '15px 20px',
                          background: 'white',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: (isAssignedToThisTemplate || hasAnyAssignment) ? 0.6 : 1,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', marginBottom: '3px' }}>
                            {user.displayName}
                            {isAssignedToThisTemplate && <span style={{ marginLeft: '8px', color: '#059669', fontSize: '13px', fontWeight: '600' }}>✓ Assigned</span>}
                            {!isAssignedToThisTemplate && hasAnyAssignment && <span style={{ marginLeft: '8px', color: '#f59e0b', fontSize: '13px', fontWeight: '600' }}>⚠ Other Template</span>}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {user.email}
                            {user.jobTitle && ` • ${user.jobTitle}`}
                          </div>
                        </div>
                        {(isAssignedToThisTemplate || hasAnyAssignment) ? (
                          <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>{isAssignedToThisTemplate ? 'Already assigned' : 'Assigned elsewhere'}</div>
                        ) : (
                          <button
                            onClick={() => toggleUserSelection(user.id)}
                            style={{
                              padding: '8px 16px',
                              background: isSelected ? '#2563eb' : '#e5e7eb',
                              color: isSelected ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: '500',
                            }}
                          >
                            {isSelected ? '✓ Selected' : 'Select'}
                          </button>
                        )}
                      </div>
                  );
                })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setShowUserModal(false);
                      setSelectedUserIds(new Set());
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processSelectedUsers}
                    disabled={processingUsers || selectedUserIds.size === 0}
                    style={{
                      padding: '10px 20px',
                      background: processingUsers || selectedUserIds.size === 0 ? '#9ca3af' : '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: processingUsers || selectedUserIds.size === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {processingUsers ? 'Processing...' : `OK (${selectedUserIds.size})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Groups Modal */}
      {showSyncGroupsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            maxHeight: '80vh',
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 10px' }}>Select Groups to Sync</h3>
              <p style={{ color: '#666', margin: '0 0 8px', fontSize: '14px' }}>
                Choose which groups and their members to sync from Microsoft 365
              </p>
              <p style={{ color: '#f59e0b', margin: 0, fontSize: '13px', fontWeight: '500' }}>
                ⚠️ Make sure users are synced first for accurate member counts
              </p>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={syncGroupsSearch}
                  onChange={(e) => setSyncGroupsSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                {selectedGroupIds.size} of {filteredM365Groups.length} group{filteredM365Groups.length !== 1 ? 's' : ''} selected
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredM365Groups.map((group) => (
                  <label
                    key={group.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      background: selectedGroupIds.has(group.id) ? '#eff6ff' : '#f9fafb',
                      border: `2px solid ${selectedGroupIds.has(group.id) ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.has(group.id)}
                      onChange={() => toggleGroupSelection(group.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{group.displayName}</div>
                      {group.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {group.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSyncGroupsModal(false);
                  setAvailableM365Groups([]);
                  setSelectedGroupIds(new Set());
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSyncGroups}
                disabled={syncingGroups || selectedGroupIds.size === 0}
                style={{
                  padding: '10px 20px',
                  background: syncingGroups || selectedGroupIds.size === 0 ? '#ccc' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: syncingGroups || selectedGroupIds.size === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                }}
              >
                {syncingGroups ? 'Syncing...' : `Sync ${selectedGroupIds.size} Group${selectedGroupIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {showGroupMembersModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowGroupMembersModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>{selectedGroupName} - Members</h3>
              <button
                onClick={() => setShowGroupMembersModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              All members of this group are assigned this template
            </p>

            {loadingGroupMembers ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading members...</div>
            ) : selectedGroupMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No members in this group</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto' }}>
                {selectedGroupMembers.map((member: any) => (
                  <div
                    key={member.id}
                    style={{
                      padding: '15px 20px',
                      background: 'white',
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '3px' }}>
                      {member.user.displayName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {member.user.email}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {showProcessingModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              minWidth: '320px',
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e5e7eb',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
              {processingMessage}
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
              Please wait...
            </p>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
